import { Card, Grid, Icon, IconButton, Switch, Autocomplete, Chip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import * as Yup from "yup";

const forcaOptions = [
    { id: 0, label: "0 - Neutro/Nenhum", color: "#9e9e9e" },
    { id: 1, label: "1 - Fraco/Frio", color: "#03a9f4" },
    { id: 2, label: "2 - Médio/Morno", color: "#ff9800" },
    { id: 3, label: "3 - Forte/Quente", color: "#f44336" }
];

const StatusFichaCreateEdit = ({ id, closeCreateEdit }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const { userPerfils } = useUser();
    const canEditAllEquipes = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || (typeof p === 'string' ? p : "")).toUpperCase();
        const tipo = p?.tipoPerfil;
        const cod = p?.codPerfil || p?.cvCodPerfil;
        return tipo === 1 || tipo === 4 || tipo === 6 || 
               cod === 1 || cod === 4 || cod === 15 ||
               pName.includes("ADMIN") || pName.includes("GESTOR") || pName.includes("GERENTE") || pName.includes("SUPORTE");
    });

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [equipesList, setEquipesList] = useState([]);
    const [myEquipes, setMyEquipes] = useState([]);

    useEffect(() => {
        get("/equipe/list-all").then((data) => {
            setEquipesList(data?.list || data?.data || (Array.isArray(data) ? data : []));
        });

        get("/equipe/minhas-equipes").then((res) => {
            setMyEquipes(res?.list || res?.data || (Array.isArray(res) ? res : []));
        });
    }, []);

    const handleSumit = (values, actions) => {
        postNoResponse("/status-ficha/save", values)
            .then(() => {
                successAlert(!id ? "Criado com sucesso!" : "Alterações salvas!", closeCreateEdit);
            }).catch(() => {
                errorAlert(!id ? "Erro ao criar!" : "Erro ao salvar alterações.");
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    return (
        <Card id="status-ficha-info" sx={{ background: darkMode ? "#4B4F5C" : "#ffffff" }}>
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                    <MDTypography variant="h5" color={darkMode ? "white" : "inherit"}><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6" color={darkMode ? "white" : "inherit"}>{id ? "Editar" : "Novo"} Status</MDTypography>
            </MDBox>
            <Formik
                initialValues={{ id: null, ccNome: '', cvAtivo: true, cvIndGlobal: false, cvIndSistema: false, equipes: [], forca: 0 }}
                validationSchema={Yup.object().shape({
                    ccNome: Yup.string().required("Campo Obrigatório.")
                })}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSumit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form autoComplete="off">
                        <FetchAndSetData id={id} setIsLoadingInfo={setIsLoadingInfo} closeCreateEdit={closeCreateEdit} />
                        <MDBox px={3} pb={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={8}>
                                    <MDInput
                                        fullWidth
                                        type="text"
                                        label="Nome"
                                        name="ccNome"
                                        variant="standard"
                                        value={values.ccNome}
                                        onChange={(e) => setFieldValue("ccNome", e.target.value)}
                                        placeholder="Informe o nome..."
                                        error={errors.ccNome?.length > 0 && touched.ccNome}
                                        required
                                        disabled={isLoadingInfo}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        options={forcaOptions}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        value={forcaOptions.find(o => o.id === values.forca) || forcaOptions[0]}
                                        onChange={(e, value) => setFieldValue("forca", value ? value.id : 0)}
                                        disabled={isLoadingInfo}
                                        renderInput={(params) => (
                                            <MDInput {...params} variant="standard" label="Nível do Termômetro" />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: option.color, marginRight: 8 }} />
                                                {option.label}
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Autocomplete
                                        multiple
                                        options={equipesList}
                                        getOptionLabel={(option) => option.nome || option.ccNome || option.id || ""}
                                        isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                                        value={values.equipes || []}
                                        disabled={isLoadingInfo}
                                        onChange={(event, newValue) => {
                                            let finalSelection = newValue;
                                            if (!canEditAllEquipes) {
                                                // Usuário comum não pode remover outras equipes que já estavam vinculadas
                                                const otherEquipesAlreadySelected = (values.equipes || []).filter(g => 
                                                    !myEquipes.some(me => String(me.id) === String(g.id))
                                                );
                                                otherEquipesAlreadySelected.forEach(otherG => {
                                                    if (!finalSelection.some(sel => String(sel.id) === String(otherG.id))) {
                                                        finalSelection.push(otherG);
                                                    }
                                                });
                                            }
                                            setFieldValue("equipes", finalSelection);
                                            if (finalSelection.length > 0) {
                                                setFieldValue("cvIndGlobal", false);
                                            }
                                        }}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const isOtherEquipe = !canEditAllEquipes && !myEquipes.some(me => String(me.id) === String(option.id));
                                                const tagProps = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={option.id}
                                                        label={option.nome || option.ccNome || option.id}
                                                        size="small"
                                                        color={isOtherEquipe ? "default" : "info"}
                                                        {...tagProps}
                                                        onDelete={isOtherEquipe ? undefined : tagProps.onDelete}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => (
                                            <MDInput 
                                                {...params} 
                                                variant="standard" 
                                                label="Equipes Permitidas (Deixe vazio para Todas / Global)" 
                                                placeholder="Selecione as equipes..." 
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={canEditAllEquipes ? 4 : 12} display="flex" alignItems="center" pt={3} pl={1}>
                                    <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Inativo</MDTypography>
                                    <Switch 
                                        checked={values.cvAtivo || false} 
                                        onChange={(event) => setFieldValue('cvAtivo', event.target.checked)} 
                                    />
                                    <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Ativo</MDTypography>
                                </Grid>
                                {canEditAllEquipes && (
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={3} pl={1}>
                                        <Switch 
                                            checked={values.cvIndGlobal || false} 
                                            onChange={(event) => {
                                                const checked = event.target.checked;
                                                setFieldValue('cvIndGlobal', checked);
                                                if (checked) {
                                                    setFieldValue('equipes', []);
                                                }
                                            }} 
                                        />
                                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"} sx={{ ml: 1 }}>
                                            Global (Todas as Equipes)
                                        </MDTypography>
                                    </Grid>
                                )}
                                {canEditAllEquipes && (
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={3} pl={1}>
                                        <Switch 
                                            checked={values.cvIndSistema || false} 
                                            onChange={(event) => setFieldValue('cvIndSistema', event.target.checked)} 
                                        />
                                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"} sx={{ ml: 1 }}>
                                            Status do Sistema
                                        </MDTypography>
                                    </Grid>
                                )}
                            </Grid>

                            <MDBox 
                                display="flex" 
                                justifyContent="flex-end" 
                                mt={4} 
                                pt={2}
                                sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0" }}
                            >
                                <MDButton 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={closeCreateEdit} 
                                    sx={{ 
                                        mr: 2, 
                                        color: darkMode ? "#ffffff !important" : undefined, 
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : undefined,
                                        "&:hover": {
                                            borderColor: darkMode ? "#ffffff !important" : undefined,
                                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : undefined
                                        }
                                    }}
                                >
                                    Cancelar
                                </MDButton>
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    type="submit" 
                                    disabled={isSubmitting || isLoadingInfo}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important",
                                        fontWeight: "bold",
                                        boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                        "&:hover": {
                                            backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                        }
                                    }}
                                >
                                    Salvar
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();
    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/status-ficha/by-id/${id}`)
                .then(data => {
                    setFieldValue('id', data.id || data.cvId);
                    setFieldValue('ccNome', data.ccNome);
                    setFieldValue('cvAtivo', data.cvAtivo);
                    setFieldValue('cvIndGlobal', data.cvIndGlobal);
                    setFieldValue('cvIndSistema', data.cvIndSistema);
                    setFieldValue('equipes', data.equipes || []);
                    setFieldValue('forca', data.forca || 0);
                    setIsLoadingInfo(false);
                }).catch(() => errorAlert("Não encontrado!", closeCreateEdit));
        }
    }, [id]);
    return null;
};

StatusFichaCreateEdit.propTypes = {
    id: PropTypes.any,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(StatusFichaCreateEdit);
